/**
 * The reporter's name on a ticket, with a contact card that opens on hover or
 * keyboard focus: title, department, how to reach them, and for store staff the
 * store (phone, local time, hours, manager). The details come from
 * `GET /v1/servicedesk/people/contact`, fetched once per person while the page
 * stays open. If the lookup is missing or fails the card says so and still
 * offers the email address the ticket already carries.
 *
 * The card is drawn in a portal at fixed position so a scrolling grid cannot
 * clip it, and it stays open while the pointer is over it so its links can be
 * clicked.
 */

import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Clock, Hash, Mail, MessageSquare, Phone, Smartphone, Store } from 'lucide-react';
import { usePersonContact } from '@/lib/useServiceDesk';
import type { SdPersonContact } from '@/lib/serviceDeskTypes';
import { SD_MUTED } from './sdStyles';

const OPEN_DELAY_MS = 180;
const CLOSE_DELAY_MS = 160;
const CARD_WIDTH = 320;

/** The zone abbreviation the api sends after the clock, e.g. `PT` from `8:00 AM PT`. */
function zoneFromLabel(label: string | null | undefined): string {
  const m = /\d\s*(?:AM|PM)\s+(\S.*)$/i.exec((label ?? '').trim());
  return m?.[1]?.trim() ?? '';
}

/**
 * The store's live clock with its zone, e.g. `8:00 AM PT`, shown once. The clock
 * is formatted here so it ticks; the zone comes from the api's label, or from the
 * browser when the label is missing. Falls back to the api's own label if the
 * zone name is unknown to the browser.
 */
function formatStoreTime(
  timezone: string | null | undefined,
  label: string | null | undefined,
  now = new Date(),
): string {
  if (timezone) {
    try {
      const clock = new Intl.DateTimeFormat('en-US', { timeZone: timezone, hour: 'numeric', minute: '2-digit' }).format(now);
      const zone =
        zoneFromLabel(label) ||
        new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName: 'short' })
          .formatToParts(now)
          .find((p) => p.type === 'timeZoneName')?.value ||
        '';
      return zone ? `${clock} ${zone}` : clock;
    } catch {
      // Unknown zone name: use what the api sent.
    }
  }
  return label ?? '';
}

function Line({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <li className="flex items-start gap-2 text-[13px] text-amp-ink dark:text-amp-dark-ink">
      <span className={`mt-0.5 shrink-0 ${SD_MUTED}`}>{icon}</span>
      <span className="min-w-0 break-words">{children}</span>
    </li>
  );
}

/** The mail glyph is itself a mailto link; the address beside it is the keyboard stop. */
function MailIcon({ address }: { address: string }) {
  return (
    <a href={`mailto:${address}`} tabIndex={-1} aria-hidden title="Send an email" className="hover:text-amp-ink">
      <Mail className="h-3.5 w-3.5" />
    </a>
  );
}

const LINK = 'text-amp-olive-deep underline-offset-2 hover:underline dark:text-amp-olive-dark';

function StoreBlock({ store }: { store: NonNullable<SdPersonContact['store']> }) {
  // The clock ticks while the card is open so "local time" stays true.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = window.setInterval(() => setNow(new Date()), 10_000);
    return () => window.clearInterval(t);
  }, []);
  const time = formatStoreTime(store.timezone, store.local_time_label, now);
  const storeTitle = [store.store_no, store.name].filter(Boolean).join(' ');

  return (
    <div className="mt-3 border-t border-amp-line pt-3 dark:border-amp-dark-line">
      <p className={`mb-1.5 text-[11px] font-semibold uppercase tracking-caps-pill ${SD_MUTED}`}>Store</p>
      <ul className="flex flex-col gap-1.5">
        {storeTitle && <Line icon={<Store className="h-3.5 w-3.5" aria-hidden />}>{storeTitle}</Line>}
        {store.phone && (
          <Line icon={<Phone className="h-3.5 w-3.5" aria-hidden />}>
            <a href={`tel:${store.phone}`} className={LINK}>
              {store.phone}
            </a>
          </Line>
        )}
        {(time || store.hours_today) && (
          <Line icon={<Clock className="h-3.5 w-3.5" aria-hidden />}>
            {time && <span data-store-time>{time}</span>}
            {store.open_now !== null && store.open_now !== undefined && (
              <span
                className={`ml-2 rounded-pill px-1.5 py-0.5 text-[11px] font-semibold ${
                  store.open_now
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                    : 'bg-slate-100 text-slate-700 dark:bg-slate-800/60 dark:text-slate-300'
                }`}
              >
                {store.open_now ? 'Open now' : 'Closed now'}
              </span>
            )}
            {store.hours_today && <span className={`block ${SD_MUTED}`}>Today {store.hours_today}</span>}
          </Line>
        )}
        {store.manager_name && (
          <Line icon={<Mail className="h-3.5 w-3.5" aria-hidden />}>
            <span>Manager {store.manager_name}</span>
            {store.manager_email && (
              <a href={`mailto:${store.manager_email}`} className={`block ${LINK}`}>
                {store.manager_email}
              </a>
            )}
          </Line>
        )}
      </ul>
    </div>
  );
}

function CardBody({
  email,
  name,
  query,
}: {
  email: string;
  name: string;
  query: ReturnType<typeof usePersonContact>;
}) {
  const c = query.data;
  const subtitle = [c?.title, c?.department].filter(Boolean).join(' - ');
  return (
    <>
      <p className="text-sm font-semibold text-amp-ink dark:text-amp-dark-ink">{c?.name || name}</p>
      {subtitle && <p className={`text-[12px] ${SD_MUTED}`}>{subtitle}</p>}
      {query.isLoading ? (
        <p className={`mt-2 text-[13px] ${SD_MUTED}`}>Loading contact details...</p>
      ) : !c ? (
        <>
          <p className={`mt-2 text-[13px] ${SD_MUTED}`}>Contact details unavailable</p>
          <ul className="mt-2 flex flex-col gap-1.5">
            <Line icon={<MailIcon address={email} />}>
              <a href={`mailto:${email}`} className={LINK}>
                {email}
              </a>
            </Line>
          </ul>
        </>
      ) : (
        <>
          <ul className="mt-2 flex flex-col gap-1.5">
            <Line icon={<MailIcon address={c.email || email} />}>
              <a href={`mailto:${c.email || email}`} aria-label={`Email ${c.name || name}`} className={LINK}>
                {c.email || email}
              </a>
            </Line>
            {c.phone && (
              <Line icon={<Phone className="h-3.5 w-3.5" aria-hidden />}>
                <a href={`tel:${c.phone}`} className={LINK}>
                  {c.phone}
                </a>
              </Line>
            )}
            {c.mobile && (
              <Line icon={<Smartphone className="h-3.5 w-3.5" aria-hidden />}>
                <a href={`tel:${c.mobile}`} className={LINK}>
                  {c.mobile}
                </a>
                <span className={SD_MUTED}> mobile</span>
              </Line>
            )}
            {c.extension && (
              <Line icon={<Hash className="h-3.5 w-3.5" aria-hidden />}>
                <span>Ext. {c.extension}</span>
              </Line>
            )}
            {c.teams_chat_url && (
              <Line icon={<MessageSquare className="h-3.5 w-3.5" aria-hidden />}>
                <a href={c.teams_chat_url} target="_blank" rel="noreferrer" className={LINK}>
                  Chat in Teams
                </a>
              </Line>
            )}
          </ul>
          {c.store && <StoreBlock store={c.store} />}
        </>
      )}
    </>
  );
}

export function ReporterContact({
  name,
  email,
  className,
  fallback = '--',
}: {
  name?: string | null;
  email?: string | null;
  className?: string;
  /** Shown when the ticket has no reporter name. */
  fallback?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const anchor = useRef<HTMLSpanElement | null>(null);
  const openTimer = useRef<number | undefined>(undefined);
  const closeTimer = useRef<number | undefined>(undefined);
  const card = useRef<HTMLDivElement | null>(null);
  const cardId = useId();
  const query = usePersonContact(email, open);

  const clearTimers = () => {
    window.clearTimeout(openTimer.current);
    window.clearTimeout(closeTimer.current);
  };
  const show = useCallback((delay: number) => {
    window.clearTimeout(closeTimer.current);
    window.clearTimeout(openTimer.current);
    openTimer.current = window.setTimeout(() => setOpen(true), delay);
  }, []);
  const hide = useCallback((delay: number) => {
    window.clearTimeout(openTimer.current);
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpen(false), delay);
  }, []);
  useEffect(() => clearTimers, []);

  // Place the card under the name, flipped above or nudged left when it would leave the screen.
  useLayoutEffect(() => {
    if (!open || !anchor.current) return;
    const r = anchor.current.getBoundingClientRect();
    const margin = 8;
    const left = Math.max(margin, Math.min(r.left, window.innerWidth - CARD_WIDTH - margin));
    const below = r.bottom + 6;
    const top = below + 280 > window.innerHeight && r.top > 300 ? Math.max(margin, r.top - 6 - 280) : below;
    setPos({ top, left });
  }, [open]);

  // Escape closes, and so does scrolling the page out from under the card.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onScroll = () => setOpen(false);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  if (!name) return <span>{fallback}</span>;
  if (!email) return <span className={className}>{name}</span>;

  return (
    <>
      <span
        ref={anchor}
        tabIndex={0}
        aria-describedby={open ? cardId : undefined}
        onMouseEnter={() => show(OPEN_DELAY_MS)}
        onMouseLeave={() => hide(CLOSE_DELAY_MS)}
        onFocus={() => show(0)}
        onBlur={() => hide(CLOSE_DELAY_MS)}
        onKeyDown={(e) => {
          // Enter or the down arrow moves into the card so its links can be used from the keyboard.
          if (e.key === 'Enter' || e.key === 'ArrowDown') {
            e.preventDefault();
            setOpen(true);
            window.setTimeout(() => card.current?.querySelector<HTMLElement>('a[href]:not([tabindex="-1"])')?.focus(), 60);
          }
        }}
        data-reporter-contact
        className={`cursor-help rounded-sm underline decoration-dotted decoration-amp-ink-muted underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-amp-olive ${className ?? ''}`}
      >
        {name}
      </span>
      {open &&
        pos &&
        createPortal(
          <div
            id={cardId}
            ref={card}
            role="group"
            aria-label={`Contact details for ${name}`}
            data-reporter-card
            style={{ position: 'fixed', top: pos.top, left: pos.left, width: CARD_WIDTH, zIndex: 80 }}
            onMouseEnter={() => show(0)}
            onMouseLeave={() => hide(CLOSE_DELAY_MS)}
            onFocus={() => show(0)}
            onBlur={() => hide(CLOSE_DELAY_MS)}
            className="rounded-lg border border-amp-line bg-amp-white p-3 shadow-lg dark:border-amp-dark-line dark:bg-amp-dark-surface"
          >
            <CardBody email={email} name={name} query={query} />
          </div>,
          document.body,
        )}
    </>
  );
}
